import { cn, Tooltip } from '@nextui-org/react'
import { FiAlertTriangle } from '@react-icons/all-files/fi/FiAlertTriangle'
import { FiFileText } from '@react-icons/all-files/fi/FiFileText'
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { sanitizeDownloadFileName } from '~/components/common/dropdowns/downloadFilenames'
import { prepareDocMarkdown } from '~/components/documents/DocMarkdown'
import PaginatedMarkdown from '~/components/documents/PaginatedDocMarkdown'

// Image rendering note: large inline `data:image/...;base64,…` payloads are
// LEFT INTACT in the markdown text. Earlier we rewrote them to `blob:` URLs
// here for parse/decoding wins, but `blob:` URLs are document-scoped — they
// die the moment the page document does, and any stale render pointing at
// them ends up broken on a normal refresh while a hard refresh accidentally
// recovers (because everything mounts fresh). Keeping the markdown canonical
// `data:` keeps refresh behaviour correct. Image-side perf (deferring the
// heavy decode so the doc text paints first) lives in
// `packages/app/src/components/markdown/img.tsx` instead.

// ─── Shared download dropdowns (lazy) ────────────────────────────────────────
//
// Both `DownloadDocumentDropdown` and `DownloadAllDocsDropdown` eagerly import
// `docx` + `jspdf` + `html2canvas` + the full `unified` markdown pipeline at
// the top of their files (several MB of JS). Wrapping them in `React.lazy`
// keeps that bundle out of the synchronous import graph so the docs tab
// (and any other consumer) paints immediately on tab switch; the icons swap
// in once the chunk arrives.
//
// These wrappers are exported so `TaskResultsView` can reuse the SAME lazy
// references for its other tabs (results / code-generation-summary). Each
// `React.lazy()` call has its own internal load-state, so two consumers each
// calling `lazy(() => import('X'))` would each pay first-Suspense cost
// independently — sharing a single wrapper means one cached state across
// the whole app, which also keeps test ordering deterministic.

export const DownloadDocumentDropdown = lazy(() =>
  import('~/components/common/dropdowns/DownloadDocumentDropdown').then(m => ({
    default: m.DownloadDocumentDropdown
  }))
)

export const DownloadAllDocsDropdown = lazy(() =>
  import('~/components/common/dropdowns/DownloadAllDocsDropdown').then(m => ({
    default: m.DownloadAllDocsDropdown
  }))
)

/** Stable placeholder rendered inside `<Suspense fallback={...}>` while the
 *  download chunk loads. Matches the icon's footprint so the surrounding
 *  layout doesn't shift when the real component swaps in. */
export const DownloadDropdownPlaceholder = (
  <div className='h-[28px] w-[28px] shrink-0' aria-hidden='true' />
)

// ─── Markdown pre-processing ─────────────────────────────────────────────────

/** Normalizes raw text into something `react-markdown` can render correctly.
 *
 *  Three cases worth massaging before the markdown parser sees the content:
 *  - A bare base64 PNG / JPEG payload (e.g. an agent dumped image bytes
 *    instead of a markdown image link). Wrap it in `![image](data:...)`.
 *  - A bare DataWeave snippet that isn't already in a fenced code block.
 *    Wrap it in a ```dwl fence so the syntax highlighter picks it up.
 *  - Anything else: pass through untouched.
 *
 *  Exported so `TaskResultsView`'s `SummaryContent` can apply the same
 *  heuristics; both views are operating on the same agent-produced markdown. */
// Single source of truth for doc canonicalization is `prepareDocMarkdown` in
// markdownRules. Re-exported under the historical name so existing importers
// (ReviewBrief, TaskResultsView) and their test mocks stay stable.
export function preprocessContent(content: string): string {
  return prepareDocMarkdown(content)
}

// ─── Types ───────────────────────────────────────────────────────────────────

export type EntryKind = 'docs' | 'risks'

/** A single endpoint row in the sidebar. Risks are a strict subset of docs in
 *  the agent output, but the proto shape doesn't guarantee that, so we tolerate
 *  doc-only, risk-only, and both-present cases. */
interface EndpointRow {
  key: string
  doc?: { markdown: string; index: number }
  risk?: { markdown: string; index: number }
}

interface Selection {
  key: string
  kind: EntryKind
}

// ─── Theme ───────────────────────────────────────────────────────────────────

const KIND_THEME: Record<
  EntryKind,
  {
    label: string
    Icon: typeof FiFileText
    /** Tailwind classes for the active pill — both the active button inside
     *  the toggle and the lone single-kind chip share these. */
    activePill: string
    filenamePrefix: 'docs' | 'risks'
    bulkTestId: string
  }
> = {
  docs: {
    label: 'Doc',
    Icon: FiFileText,
    activePill: 'bg-tokens-status-link-base text-tokens-status-link-3',
    filenamePrefix: 'docs',
    bulkTestId: 'docs-download-all'
  },
  risks: {
    label: 'Risk',
    Icon: FiAlertTriangle,
    activePill: 'bg-tokens-status-warning-3 text-tokens-status-warning-1',
    filenamePrefix: 'risks',
    bulkTestId: 'risks-download-all'
  }
}

// ─── Endpoint title ──────────────────────────────────────────────────────────

/** Subtle per-category tint for the `(HTTP)` / `(SOAP)` / … chip. Keys are
 *  kept broad so unknown categories simply fall through to the neutral tone
 *  rather than throwing or producing an ugly empty slot. */
const CATEGORY_CHIP_THEME: Record<string, string> = {
  HTTP: 'bg-tokens-status-link-base text-tokens-status-link-3',
  SOAP: 'bg-tokens-status-success-3 text-tokens-status-success-1',
  EVENT: 'bg-tokens-status-warning-3 text-tokens-status-warning-1',
  TRIGGER: 'bg-tokens-status-warning-3 text-tokens-status-warning-1',
  LISTENER: 'bg-tokens-status-link-base text-tokens-status-link-3'
}

const NEUTRAL_CHIP = 'bg-tokens-neutral-fill-fill-4 text-tokens-neutral-text-secondary'

/** Endpoint keys arrive as `Name(CATEGORY)` — e.g. `CreateUser(HTTP)`. We
 *  split the trailing category into a small tinted chip so the interface
 *  name reads as the primary label and the protocol is a quiet secondary
 *  tag rather than stray parentheses in the middle of the title. Keys that
 *  don't match the shape render untouched. */
function EndpointTitle({ endpointKey }: { endpointKey: string }) {
  const match = /^(.+?)\s*\(([A-Za-z][A-Za-z0-9_-]*)\)\s*$/.exec(endpointKey)
  if (!match) {
    return (
      <h3 className='min-w-0 flex-1 truncate font-mono text-[13px] font-semibold text-tokens-neutral-text-primary'>
        {endpointKey}
      </h3>
    )
  }
  const [, name, category] = match
  const chipTone = CATEGORY_CHIP_THEME[category.toUpperCase()] ?? NEUTRAL_CHIP
  return (
    <h3
      title={endpointKey}
      // `overflow-hidden` is the safety net: the chip is `shrink-0` so it
      // stays at full width even when this h3 has been flex-squeezed below
      // its content. Without `overflow-hidden` the chip's overflow paints
      // outside the h3's box and ends up sitting underneath the download
      // buttons in the right half of the header. Clipping at the box edge
      // makes the chip cleanly disappear instead.
      className='min-w-0 flex-1 flex items-center gap-2 overflow-hidden'
    >
      <span className='min-w-0 truncate font-mono text-[13.5px] font-semibold tracking-tight text-tokens-neutral-text-primary'>
        {name}
      </span>
      <span
        className={cn(
          'shrink-0 rounded-md px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.08em] leading-none',
          chipTone
        )}
      >
        {category}
      </span>
    </h3>
  )
}

// ─── Detail pane ─────────────────────────────────────────────────────────────

/** Right-pane content for a selected endpoint. When the endpoint has both a
 *  doc and a risk, the header exposes a [Doc | Risk] segment toggle and the
 *  per-entry + bulk download controls re-context to whichever kind is active. */
interface MarkdownDetailPaneProps {
  endpointKey: string
  activeKind: EntryKind
  onChangeKind: (kind: EntryKind) => void
  hasDoc: boolean
  hasRisk: boolean
  activeMarkdown: string
  /** Full set of doc entries — used by the bulk "Download all" dropdown when
   *  the active kind is docs. */
  allDocs: Array<[string, string]>
  /** Full set of risk entries — used by the bulk "Download all" dropdown when
   *  the active kind is risks. */
  allRisks: Array<[string, string]>
  /** True once the user has opened the Risk view for this endpoint at least
   *  once (persisted across sessions per task). When true, the attention-grab
   *  pulse on the risk toggle stays quiet — the user has already been here. */
  hasViewedRisk: boolean
}

const MarkdownDetailPane = memo(function MarkdownDetailPane({
  endpointKey,
  activeKind,
  onChangeKind,
  hasDoc,
  hasRisk,
  activeMarkdown,
  allDocs,
  allRisks,
  hasViewedRisk
}: MarkdownDetailPaneProps) {
  // Heavy work — both preprocessContent (regex scans + string ops) and the
  // downstream markdown re-parse — is keyed on activeMarkdown. Memo so it
  // only re-runs when the markdown actually changes (not e.g. when the
  // parent's tab-toggle state changes). The markdown is intentionally NOT
  // mutated to swap inline `data:image…` URIs for `blob:` URLs here — see
  // the long comment at the top of this file.
  const processedContent = useMemo(() => preprocessContent(activeMarkdown), [activeMarkdown])
  const theme = KIND_THEME[activeKind]
  const HeaderIcon = theme.Icon
  const bulkEntries = activeKind === 'docs' ? allDocs : allRisks
  const showDownloadAll = bulkEntries.length > 1
  const showKindToggle = hasDoc && hasRisk

  // Per-(doc, kind) scroll memory. Switching to a different doc/risk restores
  // its previously saved offset; first visit starts at the top. The map lives
  // for the lifetime of this pane only — leaving and re-opening the Docs tab
  // unmounts it and the offsets are forgotten, which is the desired scope.
  // Streaming content growth within the active doc is intentionally NOT a
  // dependency, so the user's scroll position is preserved as the doc grows.
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const scrollPositionsRef = useRef<Map<string, number>>(new Map())
  const previousScrollKeyRef = useRef<string | null>(null)
  const scrollKey = `${endpointKey}::${activeKind}`
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const prevKey = previousScrollKeyRef.current
    if (prevKey && prevKey !== scrollKey) {
      scrollPositionsRef.current.set(prevKey, el.scrollTop)
    }
    el.scrollTop = scrollPositionsRef.current.get(scrollKey) ?? 0
    previousScrollKeyRef.current = scrollKey
  }, [scrollKey])

  // Horror animation lifecycle:
  //   - Plays only while the user is NOT on the risks tab (drawing attention
  //     to it). Once they land on risks, the loop should quiet down.
  //   - Removing the animation class mid-cycle would snap the icon back to
  //     its resting state with a visible jump. Instead, when a stop is
  //     requested we let the current iteration finish and only drop the
  //     class on `animationiteration`. Re-entering "should pulse" cancels
  //     a pending stop and resumes immediately.
  //   - Also a partial accessibility mitigation: motion-sensitive users who
  //     spend any real time on the risks tab won't see continuous pulsing.
  //   - One-shot per endpoint: once the user has actually opened the risks
  //     view for this endpoint (`hasViewedRisk`), the pulse stays off
  //     forever for it — they've been warned, no need to keep yelling.
  //     That signal is persisted in localStorage by the parent panel so
  //     reloads / tab re-mounts don't resurrect the animation.
  const shouldPulse = showKindToggle && activeKind !== 'risks' && !hasViewedRisk
  const [isPulsing, setIsPulsing] = useState(shouldPulse)
  const stopPendingRef = useRef(false)
  useEffect(() => {
    if (shouldPulse) {
      stopPendingRef.current = false
      setIsPulsing(true)
    } else {
      stopPendingRef.current = true
    }
  }, [shouldPulse])
  const handleHorrorIteration = useCallback(() => {
    if (stopPendingRef.current) {
      stopPendingRef.current = false
      setIsPulsing(false)
    }
  }, [])

  return (
    <div className='flex flex-1 min-h-0 flex-col overflow-hidden bg-white'>
      <div className='flex items-center justify-between gap-3 border-b border-tokens-neutral-border-2 bg-white pl-10 pr-6 py-4'>
        <div className='flex min-w-0 flex-1 items-center gap-8'>
          {showKindToggle ? (
            // Square segment with stacked icon-over-label. The track has a
            // subtle inner shadow so it reads as a recessed well; the sliding
            // bubble is solid white with a soft drop shadow so it reads as a
            // raised chip sitting inside the well. The accent color
            // (purple↔amber) lives on the active icon + label instead of the
            // bubble, which keeps the control feeling calm while still
            // signaling the kind.
            <div
              role='tablist'
              aria-label='Select doc or risk view'
              className='relative inline-flex h-12 shrink-0 items-center rounded-[12px] bg-tokens-neutral-fill-fill-4 p-0.5 shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)]'
            >
              <div
                aria-hidden='true'
                className={cn(
                  'pointer-events-none absolute top-0.5 left-0.5 h-11 w-11 rounded-[10px] bg-white shadow-segment transition-transform duration-200 ease-out',
                  activeKind === 'docs' ? 'translate-x-0' : 'translate-x-11'
                )}
              />
              {(['docs', 'risks'] as const).map(kind => {
                const kindTheme = KIND_THEME[kind]
                const KindIcon = kindTheme.Icon
                const isActive = kind === activeKind
                const isRisk = kind === 'risks'
                // Both sides carry their kind color at all times — inactive
                // at a softer opacity, active at full strength — so the two
                // options read as clearly distinct colored slots rather than
                // a grey base with one highlighted button.
                //
                // Risks get a horror-game treatment: the live icon pulses red
                // with a glowing drop-shadow while a translucent crimson
                // silhouette of the same icon rises out of it like a ghost.
                // It's loud on purpose — risks shouldn't feel calm.
                const kindColor = isRisk ? 'text-red-600' : 'text-tokens-status-link-3'
                return (
                  <button
                    key={kind}
                    type='button'
                    role='tab'
                    aria-selected={isActive}
                    aria-label={kindTheme.label}
                    data-testid={`docs-detail-toggle-${kind}`}
                    onClick={() => onChangeKind(kind)}
                    className={cn(
                      'relative z-10 flex h-11 w-11 flex-col items-center justify-center gap-[3px] rounded-[10px] transition-opacity duration-200 ease-out',
                      kindColor,
                      isActive ? 'opacity-100' : 'opacity-55 hover:opacity-100'
                    )}
                  >
                    <span className='relative inline-flex h-[17px] w-[17px] items-center justify-center'>
                      {isRisk && isPulsing && (
                        <span
                          aria-hidden='true'
                          className='pointer-events-none absolute top-0 left-1/2 inline-flex h-[17px] w-[17px] -translate-x-1/2 items-center justify-center text-red-700 opacity-0 mix-blend-multiply animate-horror-ghost will-change-transform'
                        >
                          <KindIcon size={17} strokeWidth={2.5} />
                        </span>
                      )}
                      <KindIcon
                        size={17}
                        strokeWidth={2.5}
                        onAnimationIteration={isRisk ? handleHorrorIteration : undefined}
                        className={cn(
                          'relative',
                          isRisk && isPulsing && 'animate-horror-pulse will-change-transform'
                        )}
                      />
                    </span>
                    <span
                      className={cn(
                        'text-[9px] uppercase tracking-[0.06em] leading-none',
                        isActive ? 'font-bold' : 'font-semibold'
                      )}
                    >
                      {kindTheme.label}
                    </span>
                  </button>
                )
              })}
            </div>
          ) : (
            // Single-kind case: a square badge matching the toggle's outer
            // dimensions so the header layout stays consistent across the
            // one-kind and two-kind cases.
            <span
              aria-label={theme.label}
              title={theme.label}
              className={cn(
                'inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-[12px]',
                theme.activePill
              )}
            >
              <HeaderIcon size={20} />
            </span>
          )}
          <EndpointTitle endpointKey={endpointKey} />
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          {showDownloadAll && (
            <Suspense fallback={DownloadDropdownPlaceholder}>
              <DownloadAllDocsDropdown
                entries={bulkEntries}
                filenamePrefix={theme.filenamePrefix}
                testId={theme.bulkTestId}
                // `theme.label` is already 'Doc' / 'Risk' (singular) so the
                // dropdown's pluralisation logic produces "Doc" for 1 and
                // "Docs" / "Risks" otherwise — the user sees what kind
                // of artifact they're actually bundling rather than a
                // generic count.
                itemLabel={theme.label}
              />
            </Suspense>
          )}
          <Suspense fallback={DownloadDropdownPlaceholder}>
            <DownloadDocumentDropdown
              content={activeMarkdown}
              // Filename includes both the doc kind (`docs` / `risks`) and
              // the endpoint key so the saved file is self-describing on
              // the user's filesystem instead of every download landing as
              // a generic `document.pdf`.
              filename={sanitizeDownloadFileName(`${theme.filenamePrefix}-${endpointKey}`)}
            />
          </Suspense>
        </div>
      </div>
      <div ref={scrollRef} className='flex-1 overflow-auto pr-6 py-5'>
        {/* `paginationKey` mirrors `scrollKey` so each (endpoint, kind) pair
            keeps its own pagination memory. Loading more pages on doc A and
            switching to doc B then back to doc A restores A's last-revealed
            page count instead of dropping back to page 1. The scope is the
            DocsPanel instance — switching tabs / tasks unmounts the
            PaginatedMarkdown and the cache resets, which matches the
            desired memory boundary. */}
        <PaginatedMarkdown text={processedContent} paginationKey={scrollKey} />
      </div>
    </div>
  )
})

// ─── Sidebar leading icon ────────────────────────────────────────────────────

/** Sidebar leading icon. Three states:
 *  - doc only   → file glyph
 *  - risk only  → warning glyph (amber)
 *  - doc + risk → file glyph with a tiny amber warning tucked in the corner
 *                 signalling "there's also a risk on this endpoint"
 *
 *  The icon picks up the row's active accent colour on the selected row and
 *  a quieter neutral tone otherwise, so it reads as part of the row rather
 *  than a separate badge. */
function EndpointRowIcon({
  hasDoc,
  hasRisk,
  isRowSelected
}: {
  hasDoc: boolean
  hasRisk: boolean
  isRowSelected: boolean
}) {
  if (!hasDoc && hasRisk) {
    return <FiAlertTriangle size={15} className='shrink-0 text-tokens-status-warning-1' />
  }
  // No more link/purple accent on selected — the glass-bubble bg carries
  // the selection signal now, so the icon just steps up to the firmer
  // text-primary tone instead of switching colour family.
  const iconTone = isRowSelected
    ? 'text-tokens-neutral-icon-hover'
    : 'text-tokens-neutral-icon-default group-hover/row:text-tokens-neutral-icon-hover'
  if (hasDoc && !hasRisk) {
    return <FiFileText size={15} className={cn('shrink-0 transition-colors', iconTone)} />
  }
  return (
    <span
      className='relative inline-flex shrink-0 items-center justify-center'
      aria-label='Has doc and risk'
      title='Doc and risk available — toggle in the right pane'
    >
      <FiFileText size={15} className={cn('transition-colors', iconTone)} />
      <FiAlertTriangle
        size={8}
        strokeWidth={2.5}
        className='absolute -bottom-[2px] -right-[3px] text-tokens-status-warning-1'
      />
    </span>
  )
}

// ─── Sort ────────────────────────────────────────────────────────────────────

/** Ordering rules for the sidebar (applied in priority order):
 *
 *  1. Kind bucket: rows with BOTH doc and risk first, then doc-only, then
 *     risk-only. Surfaces the fullest endpoints at the top.
 *  2. Category (the `(HTTP)` / `(SOAP)` / … suffix): fixed protocol priority.
 *  3. HTTP verb prefix on the name (`POST /api/orders` → `POST`): fixed verb
 *     priority. Non-HTTP keys fall to the end of this level.
 *  4. Alphabetical on the full key as the final tiebreaker.
 *
 *  Unknown categories / verbs sort after all known ones so the "other" bucket
 *  is predictable rather than interleaved. */
const CATEGORY_ORDER = ['HTTP', 'SOAP', 'EVENT', 'TRIGGER', 'LISTENER'] as const
const VERB_ORDER = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const

function kindRank(row: EndpointRow): number {
  if (row.doc && row.risk) return 0
  if (row.doc) return 1
  return 2
}

/** Mirrors `EndpointTitle`'s split: non-greedy name, trailing `(CATEGORY)`.
 *  Keys that don't match (rare, e.g. legacy agent output) are treated as
 *  nameless + no category so they sink to the bottom of every tier. */
function parseKey(key: string): { name: string; category: string } {
  const match = /^(.+?)\s*\(([A-Za-z][A-Za-z0-9_-]*)\)\s*$/.exec(key)
  if (!match) return { name: key, category: '' }
  return { name: match[1], category: match[2].toUpperCase() }
}

function categoryRank(category: string): number {
  const idx = CATEGORY_ORDER.indexOf(category as (typeof CATEGORY_ORDER)[number])
  return idx === -1 ? CATEGORY_ORDER.length : idx
}

/** Matches the leading HTTP verb token in an endpoint name, e.g.
 *  `POST /api/orders` → `POST`. Requires a word boundary after the verb so
 *  names like `POSTPONE` aren't miscategorised. */
function verbRank(name: string): number {
  const upper = name.trimStart().toUpperCase()
  for (let i = 0; i < VERB_ORDER.length; i++) {
    const verb = VERB_ORDER[i]
    if (upper === verb) return i
    if (
      upper.length > verb.length &&
      upper.startsWith(verb) &&
      /\s/.test(upper.charAt(verb.length))
    ) {
      return i
    }
  }
  return VERB_ORDER.length
}

function compareRows(a: EndpointRow, b: EndpointRow): number {
  const kindDiff = kindRank(a) - kindRank(b)
  if (kindDiff !== 0) return kindDiff

  const pa = parseKey(a.key)
  const pb = parseKey(b.key)

  const catDiff = categoryRank(pa.category) - categoryRank(pb.category)
  if (catDiff !== 0) return catDiff

  const verbDiff = verbRank(pa.name) - verbRank(pb.name)
  if (verbDiff !== 0) return verbDiff

  return a.key.localeCompare(b.key, undefined, { sensitivity: 'base', numeric: true })
}

// ─── Sidebar resize ──────────────────────────────────────────────────────────

/** Horizontal resize behaviour for the sidebar / detail-pane split.
 *
 *  - Sidebar can shrink down to `SIDEBAR_MIN_WIDTH`. At that width the row
 *    label truncates to nothing and only the kind icon is visible — a
 *    clean "icon rail" mode.
 *  - Detail pane has a hard `DETAIL_MIN_WIDTH` that keeps the toggle and
 *    the two download buttons fully visible regardless of how far the user
 *    drags. The title (centre) absorbs all squish via its own `truncate`.
 *  - Default width follows the Figma review layout's 241 px sidebar.
 *  - Width lives in component state, not storage — restarting the panel
 *    (tab/task switch) resets to default. User didn't ask for persistence
 *    and the cost of re-dragging is trivial. */
const SIDEBAR_DEFAULT_WIDTH = 241
// Minimum keeps the sidebar usable, not just structurally visible. The
// original "icon-only rail" target (~72 px) collapsed every row label to
// a single character + ellipsis ("G." / "P.") — fine as a layout but
// useless for actually picking an endpoint, and tight enough that the
// header "DOCS N" count clipped past the panel edge. 180 px gives each
// row ~110 px of label space (≈ 14 mono chars at 12.5 px) which is
// enough to disambiguate similarly-named endpoints, and leaves the
// header comfortably padded.
const SIDEBAR_MIN_WIDTH = 180
// Detail pane minimum derived from the header layout: toggle (~96 px) +
// gap-8 (32) + title room (~110, see below) + gap-2 (8) + Download-all
// (~165 px, accounts for two-digit count) + gap-2 (8) + Download
// (~140 px) + pl-10/pr-6 padding (64 px) ≈ 640 px. Title room is sized
// so the longest category chip ("LISTENER", ~64 px) stays fully visible
// alongside a few characters of the truncated endpoint name — i.e. the
// (HTTP) / (SOAP) / … chip never clips at the locked-min width. The
// `overflow-hidden` on the title `<h3>` is a belt-and-braces guard so
// the chip clips cleanly at the container edge if these estimates are
// ever off, instead of bleeding into the download buttons.
const DETAIL_MIN_WIDTH = 640
const RESIZE_HANDLE_WIDTH = 5

function useSidebarResize(enabled: boolean) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT_WIDTH)
  const [isDragging, setIsDragging] = useState(false)

  // Track container width via ResizeObserver so we can clamp the sidebar
  // when the surrounding viewport changes (window resize, sidebar collapse,
  // compact-mode flip). Without this the sidebar can end up wider than the
  // container and squeeze the detail pane below its minimum.
  useEffect(() => {
    if (!enabled) return
    const el = containerRef.current
    if (!el) return
    const update = () => setContainerWidth(el.clientWidth)
    update()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [enabled])

  // Maximum sidebar width given the current container size and the
  // detail pane's minimum. Falls back to a sensible cap until the
  // ResizeObserver has reported an actual width.
  const maxSidebarWidth = Math.max(
    SIDEBAR_MIN_WIDTH,
    (containerWidth || SIDEBAR_DEFAULT_WIDTH * 2) - DETAIL_MIN_WIDTH - RESIZE_HANDLE_WIDTH
  )
  const clampedWidth = Math.min(Math.max(sidebarWidth, SIDEBAR_MIN_WIDTH), maxSidebarWidth)

  // Re-clamp stored width if the container shrank past our current value
  // (e.g. the user collapsed the app sidebar while the docs panel was
  // already at maximum). Done in render via the "adjust state during
  // render" pattern so the next paint already reflects the clamped value.
  if (enabled && sidebarWidth !== clampedWidth) {
    setSidebarWidth(clampedWidth)
  }

  const startDrag = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!enabled) return
      e.preventDefault()
      const startX = e.clientX
      const startWidth = clampedWidth
      const container = containerRef.current
      setIsDragging(true)
      // Force the col-resize cursor everywhere during drag so it doesn't
      // flicker back to the default when the pointer leaves the 5-px
      // handle. Restored on mouseup.
      const previousCursor = document.body.style.cursor
      const previousUserSelect = document.body.style.userSelect
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'

      const onMove = (ev: MouseEvent) => {
        const cw = container?.clientWidth ?? containerWidth
        const dynamicMax = Math.max(SIDEBAR_MIN_WIDTH, cw - DETAIL_MIN_WIDTH - RESIZE_HANDLE_WIDTH)
        const next = Math.min(
          Math.max(startWidth + (ev.clientX - startX), SIDEBAR_MIN_WIDTH),
          dynamicMax
        )
        setSidebarWidth(next)
      }
      const onUp = () => {
        setIsDragging(false)
        document.body.style.cursor = previousCursor
        document.body.style.userSelect = previousUserSelect
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },
    [clampedWidth, containerWidth, enabled]
  )

  return {
    containerRef,
    sidebarWidth: clampedWidth,
    isDragging,
    startDrag
  }
}

// ─── Panel ───────────────────────────────────────────────────────────────────

export interface DocsPanelProps {
  /** Per-endpoint markdown documents from `MigrationDocumentOutput.migDocsMap`. */
  docEntries: Array<[string, string]>
  /** Per-endpoint risk markdown from `MigrationDocumentOutput.migDocsRiskMap`. */
  riskEntries: Array<[string, string]>
  /** Compact mode (plugin viewports) stacks the sidebar above the detail pane
   *  instead of beside it, and caps the sidebar height at a fixed value. */
  isCompactViewport: boolean
  /** Scopes the "viewed risks" persistence so two tasks viewing the same
   *  endpoint key don't share each other's seen-state. Optional because
   *  some legacy callsites may not have a task in scope; without it the
   *  set still works in-memory for the panel's lifetime, just not across
   *  reloads. */
  taskId?: number
}

// ─── Viewed-risks persistence ───────────────────────────────────────────────
//
// Once the user has actually opened the Risks view for an endpoint, the
// attention-grab pulse on that endpoint's risk toggle goes quiet for good.
// The signal is persisted per-task in localStorage so a reload, tab
// re-mount, or task re-open doesn't bring the animation back from the
// dead. Keying on `taskId` (rather than a global set) keeps the scope
// tight: different tasks have different risk content, so "I've seen
// MyEndpoint's risk in task 17" shouldn't suppress the pulse for the same
// endpoint name in task 42 where the risk write-up may be entirely new.
const VIEWED_RISKS_STORAGE_PREFIX = 'curie:docs-panel:viewed-risks:'

function viewedRisksStorageKey(taskId: number | undefined): string | null {
  if (taskId === undefined) return null
  return `${VIEWED_RISKS_STORAGE_PREFIX}${taskId}`
}

function readViewedRisks(taskId: number | undefined): Set<string> {
  const key = viewedRisksStorageKey(taskId)
  if (!key) return new Set()
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed))
      return new Set(parsed.filter((v): v is string => typeof v === 'string'))
    return new Set()
  } catch {
    return new Set()
  }
}

function writeViewedRisks(taskId: number | undefined, viewed: Set<string>): void {
  const key = viewedRisksStorageKey(taskId)
  if (!key) return
  try {
    window.localStorage.setItem(key, JSON.stringify([...viewed]))
  } catch {
    // Quota / private-mode failures are non-fatal — at worst the pulse
    // resurrects on next reload, which is strictly less bad than crashing
    // the docs panel.
  }
}

/** Merged doc + risk panel for the task-results "Docs" tab.
 *
 *  Sidebar shows one row per endpoint. Each row's leading icon reflects which
 *  kinds are available (doc only, risk only, both). Clicking a row selects
 *  the doc by default; the right pane's segment toggle switches between doc
 *  and risk for endpoints that have both.
 */
export const DocsPanel = memo(function DocsPanel({
  docEntries,
  riskEntries,
  isCompactViewport,
  taskId
}: DocsPanelProps) {
  const rows = useMemo<EndpointRow[]>(() => {
    // Union doc + risk entries keyed by endpoint. Original doc/risk indices
    // are preserved on each slot (they drive per-entry test-ids and the
    // download dropdown's entry identity); only the sidebar display order is
    // reshuffled by `compareRows`.
    const byKey = new Map<string, EndpointRow>()
    docEntries.forEach(([key, markdown], index) => {
      byKey.set(key, { key, doc: { markdown, index } })
    })
    riskEntries.forEach(([key, markdown], index) => {
      const existing = byKey.get(key)
      if (existing) existing.risk = { markdown, index }
      else byKey.set(key, { key, risk: { markdown, index } })
    })
    return [...byKey.values()].sort(compareRows)
  }, [docEntries, riskEntries])

  const initialSelection = useMemo<Selection | null>(() => {
    const first = rows[0]
    if (!first) return null
    return { key: first.key, kind: first.doc ? 'docs' : 'risks' }
  }, [rows])
  const [selection, setSelection] = useState<Selection | null>(initialSelection)

  // Set of endpoint keys for which the user has opened the Risks view at
  // least once. Hydrated lazily from localStorage on mount and re-hydrated
  // when `taskId` changes (e.g. switching between tasks in the same
  // session keeps each task's seen-state isolated).
  const [viewedRiskKeys, setViewedRiskKeys] = useState<Set<string>>(() => readViewedRisks(taskId))
  useEffect(() => {
    setViewedRiskKeys(readViewedRisks(taskId))
  }, [taskId])

  // Whenever the active selection lands on a risk view, mark that endpoint
  // as seen and persist. Done in an effect (not in the click handler) so
  // any path that ends up showing risks — keyboard nav, programmatic kind
  // change, the auto-fallback when a doc is missing — counts as "viewed".
  useEffect(() => {
    if (!selection || selection.kind !== 'risks') return
    if (viewedRiskKeys.has(selection.key)) return
    const next = new Set(viewedRiskKeys)
    next.add(selection.key)
    setViewedRiskKeys(next)
    writeViewedRisks(taskId, next)
  }, [selection, viewedRiskKeys, taskId])

  // Keep the selection valid as the underlying entries refresh / change shape:
  // - selected key disappeared → snap to first row
  // - selected kind no longer present on its row (e.g. risk was removed) →
  //   fall back to the other kind on the same row
  useEffect(() => {
    if (!selection) {
      if (initialSelection) setSelection(initialSelection)
      return
    }
    const row = rows.find(r => r.key === selection.key)
    if (!row) {
      setSelection(initialSelection)
      return
    }
    if (selection.kind === 'docs' && !row.doc) {
      setSelection({ key: row.key, kind: 'risks' })
    } else if (selection.kind === 'risks' && !row.risk) {
      setSelection({ key: row.key, kind: 'docs' })
    }
  }, [rows, selection, initialSelection])

  const selectedRow = selection ? (rows.find(r => r.key === selection.key) ?? rows[0]) : rows[0]
  const selectedSlot =
    selection && selectedRow
      ? selection.kind === 'docs'
        ? selectedRow.doc
        : selectedRow.risk
      : undefined
  const fallbackSlot = selectedRow ? (selectedRow.doc ?? selectedRow.risk) : undefined
  const activeSlot = selectedSlot ?? fallbackSlot
  const activeKind: EntryKind =
    selectedSlot && selection ? selection.kind : selectedRow?.doc ? 'docs' : 'risks'
  const activeMarkdown = activeSlot?.markdown ?? ''

  // Defer the markdown content so clicking a sidebar entry updates the
  // highlighted row immediately, while the expensive markdown re-parse
  // happens in the background. Without this the click waits ~100–500 ms on
  // multi-MB docs before anything paints.
  const deferredKey = useDeferredValue(selectedRow?.key ?? '')
  const deferredMarkdown = useDeferredValue(activeMarkdown)
  const deferredKind = useDeferredValue(activeKind)

  // Resize is only meaningful in the side-by-side row layout. Compact mode
  // stacks sidebar above detail pane and vertical resize isn't requested.
  const { containerRef, sidebarWidth, isDragging, startDrag } = useSidebarResize(!isCompactViewport)

  if (rows.length === 0 || !selection || !selectedRow || !activeSlot) {
    return (
      <div className='flex items-center justify-center h-32 text-tokens-neutral-text-tertiary'>
        <p className='text-[13px]'>No documents or risks available.</p>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        // Keep the generated-doc surface flat. The border defines the panel
        // edge without adding a large floating-card shadow around the content.
        'relative flex flex-1 min-h-0 overflow-hidden m-2 rounded-[28px] border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white',
        isCompactViewport ? 'flex-col' : 'flex-row'
      )}
    >
      <div
        className={cn(
          // White bg matches the detail pane; the right (or bottom in compact)
          // border still carries the structural split between the two panes.
          // Keeping a single colour across both panes makes the surface read
          // as one cohesive panel instead of "list panel + content panel".
          'shrink-0 bg-tokens-neutral-bg-white flex flex-col min-h-0',
          isCompactViewport
            ? 'border-b border-tokens-neutral-border-2 max-h-[280px]'
            : 'h-full border-r border-tokens-neutral-border-2'
        )}
        style={isCompactViewport ? undefined : { width: sidebarWidth }}
      >
        <div className='flex h-[80px] shrink-0 items-center justify-between gap-2 border-b border-tokens-neutral-border-2 px-6'>
          <h2 className='min-w-0 truncate text-[16px] font-semibold leading-6 text-tokens-neutral-text-primary'>
            {riskEntries.length > 0 ? 'Assumptions & Risks' : 'Docs'}
          </h2>
          {/* `shrink-0` so the count is never the thing that clips off the
              right edge under justify-between when the sidebar is at its
              narrowest — the label truncates instead. */}
          <span className='shrink-0 text-[11px] font-medium tabular-nums text-tokens-neutral-text-tertiary'>
            {rows.length}
          </span>
        </div>
        {/* `pt-2` gives the selected-row glass-bubble shadow somewhere to
            render above the first row. Without it the row sits flush with
            the scroll container's top edge and the shadow's blur (which
            extends ~6 px above the row) gets clipped by the container's
            `overflow-y-auto`, reading as a "whited-out" top edge on the
            bubble. */}
        <div className='flex-1 min-h-0 overflow-y-auto px-4 pt-4 pb-4'>
          {rows.map(row => {
            const isRowSelected = row.key === selectedRow.key
            // Fallback when the user's currently-toggled kind isn't
            // available on the row they're clicking — prefer the doc,
            // fall back to the risk (risk-only rows are rare but legal).
            const fallbackKind: EntryKind = row.doc ? 'docs' : 'risks'
            // Carry the user's deliberate kind toggle across endpoints:
            // if they're viewing Risks on one row and click another row
            // that also has a risk, default to that row's risk. The
            // fallback only kicks in when their preferred kind doesn't
            // exist on the new row.
            const preferredKind = selection.kind
            const targetKind: EntryKind =
              preferredKind === 'risks' && row.risk
                ? 'risks'
                : preferredKind === 'docs' && row.doc
                  ? 'docs'
                  : fallbackKind
            const testId = row.doc ? `docs-item-${row.doc.index}` : `risks-item-${row.risk!.index}`

            return (
              // 1500 ms delay so the tooltip only appears on a deliberate
              // hover, not as the user is just sweeping their cursor down
              // the list. `placement='right'` keeps the popover aimed at
              // the detail pane instead of clipping off the left edge of
              // a narrow sidebar. The tooltip is the safety net for long
              // truncated endpoint names — short names that already fit
              // see a redundant-but-harmless echo.
              <Tooltip
                key={row.key}
                content={row.key}
                delay={1500}
                closeDelay={0}
                placement='right'
                offset={6}
                classNames={{
                  content:
                    'max-w-[360px] break-all rounded-[6px] bg-tokens-neutral-bg-action px-2 py-[6px] font-mono text-[11.5px] leading-[14px] text-tokens-neutral-text-white shadow-md'
                }}
              >
                <button
                  type='button'
                  data-testid={testId}
                  onClick={() => setSelection({ key: row.key, kind: targetKind })}
                  className={cn(
                    'group/row mb-2 flex h-12 w-full items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-left transition-all last:mb-0',
                    isRowSelected
                      ? // Glass bubble: a white card lifted off the white
                        // sidebar surface by a soft drop shadow + a 1-px
                        // dark hairline (delivered via the second shadow
                        // stop so it draws on the same elevation plane as
                        // the shadow itself). Reads as a raised, slightly
                        // frosted chip rather than a flat coloured tint.
                        'bg-white text-tokens-neutral-text-primary shadow-[0_2px_8px_rgba(15,23,42,0.08),0_0_0_1px_rgba(15,23,42,0.05)]'
                      : 'text-tokens-neutral-text-secondary hover:bg-tokens-neutral-fill-fill-4 hover:text-tokens-neutral-text-primary'
                  )}
                >
                  <EndpointRowIcon
                    hasDoc={!!row.doc}
                    hasRisk={!!row.risk}
                    isRowSelected={isRowSelected}
                  />
                  <span
                    className={cn(
                      'block min-w-0 flex-1 truncate font-mono text-[12.5px] leading-5',
                      isRowSelected ? 'font-semibold' : 'font-medium'
                    )}
                  >
                    {row.key}
                  </span>
                </button>
              </Tooltip>
            )
          })}
        </div>
      </div>

      {!isCompactViewport && (
        // Drag handle. Keeps a small visible accent on hover/drag so the
        // user can find it; collapses to a 1-px hairline at rest so it
        // doesn't draw the eye. `role='separator'` + aria props announce
        // it to screen readers as a resize control.
        <div
          role='separator'
          aria-orientation='vertical'
          aria-label='Resize docs sidebar'
          data-testid='docs-sidebar-resize-handle'
          onMouseDown={startDrag}
          className={cn(
            'group/resize relative shrink-0 cursor-col-resize select-none',
            'h-full',
            isDragging && 'bg-tokens-status-link-3/40'
          )}
          style={{ width: RESIZE_HANDLE_WIDTH }}
        >
          <div
            aria-hidden='true'
            className={cn(
              'absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors',
              isDragging
                ? 'bg-tokens-status-link-3'
                : 'bg-tokens-neutral-border-2 group-hover/resize:bg-tokens-status-link-3'
            )}
          />
        </div>
      )}

      <MarkdownDetailPane
        endpointKey={deferredKey}
        activeKind={deferredKind}
        onChangeKind={kind => setSelection({ key: selectedRow.key, kind })}
        hasDoc={!!selectedRow.doc}
        hasRisk={!!selectedRow.risk}
        activeMarkdown={deferredMarkdown}
        allDocs={docEntries}
        allRisks={riskEntries}
        hasViewedRisk={viewedRiskKeys.has(deferredKey)}
      />
    </div>
  )
})

export default DocsPanel
